import { forwardRef, useState, type InputHTMLAttributes, type ReactNode } from "react";
import { cn } from "@/lib/cn";

export interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, "size"> {
  label?: string;
  hint?: string;
  error?: string;
  leftIcon?: ReactNode;
  rightSlot?: ReactNode;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { label, hint, error, leftIcon, rightSlot, className, id, type, inputMode, pattern, ...rest },
  ref,
) {
  const [focused, setFocused] = useState(false);
  const inputId = id ?? `input-${Math.random().toString(36).slice(2, 9)}`;
  const isNumber = type === "number";
  const hasDecimalStep = typeof rest.step === "number"
    ? !Number.isInteger(rest.step)
    : typeof rest.step === "string" && /[.,]/.test(rest.step);

  return (
    <div className="flex flex-col gap-1.5">
      {label && (
        <label htmlFor={inputId} className="text-xs font-medium text-text-muted px-1">
          {label}
        </label>
      )}
      <div
        className={cn(
          "group flex items-center gap-2.5 h-12 rounded-2xl px-4",
          "bg-surface-2 border border-white/[0.06]",
          "transition-all duration-200",
          focused && "border-accent/40 bg-surface-3 shadow-[0_0_0_4px_rgba(255,107,53,0.08)]",
          error && "border-danger/50 shadow-[0_0_0_4px_rgba(255,77,109,0.08)]",
        )}
      >
        {leftIcon && (
          <span className="text-text-dim [&_svg]:size-4 shrink-0 group-focus-within:text-accent transition-colors">
            {leftIcon}
          </span>
        )}
        <input
          id={inputId}
          ref={ref}
          type={isNumber ? "text" : type}
          inputMode={inputMode ?? (isNumber ? (hasDecimalStep ? "decimal" : "numeric") : undefined)}
          pattern={pattern ?? (isNumber ? "[0-9]*[.,]?[0-9]*" : undefined)}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          className={cn(
            "flex-1 min-w-0 bg-transparent outline-none text-[15px] text-white",
            "placeholder:text-text-dim",
            className,
          )}
          {...rest}
        />
        {rightSlot && <span className="text-text-dim shrink-0">{rightSlot}</span>}
      </div>
      {(error || hint) && (
        <div
          className={cn(
            "text-xs px-1",
            error ? "text-danger" : "text-text-dim",
          )}
        >
          {error ?? hint}
        </div>
      )}
    </div>
  );
});
